import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@givebutter/primitives/button';
import { useMediaQuery } from '@givebutter/react-util';
import { PanelProvider, usePanelContext } from 'components/profile-panel/PanelContext';
import { ProfileColor } from 'components/profile-panel/assets/ProfileColor';
import { ProfileColorDisplay } from 'components/profile-panel/assets/ProfileColorDisplay';
import {
    ProfileCoverIcon,
    ProfileCoverImage,
} from 'components/profile-panel/assets/ProfileCoverImage';
import { ProfileEmail } from 'components/profile-panel/assets/ProfileEmail';
import { ProfileLogo, ProfileLogoDisplay } from 'components/profile-panel/assets/ProfileLogo';
import { ProfileName } from 'components/profile-panel/assets/ProfileName';
import { ProfilePhoneNumber } from 'components/profile-panel/assets/ProfilePhoneNumber';
import { ProfileTagLine } from 'components/profile-panel/assets/ProfileTagLine';
import {
    ProfileVerificationIcon,
    ProfileVerificationIconDisplay,
} from 'components/profile-panel/assets/ProfileVerificationIcon';
import { ProfileWebsite } from 'components/profile-panel/assets/ProfileWebsite';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { twMerge } from 'tailwind-merge';

const ProfilePanel = () => {
    const isMobile = useMediaQuery(`(max-width: 640px)`);
    const { accountId, cover, type } = useCurrentAccount();
    const { placeholderCover } = usePanelContext();

    const useCover = useMemo(() => cover || placeholderCover, [cover, placeholderCover]);

    if (isMobile) {
        return (
            <Link to={`/accounts/${accountId}/settings/about`}>
                <Button
                    variant="outline"
                    color="neutral"
                    className="h-fit w-full justify-start gap-3 rounded-xl p-4"
                    aria-label="Go to Account Settings"
                >
                    <ProfileLogoDisplay />
                    <div className="flex flex-col gap-1">
                        <ProfileName />
                        {type === 'nonprofit' && <ProfileVerificationIconDisplay />}
                    </div>
                </Button>
            </Link>
        );
    }
    return (
        <div
            key={`profilePanel-${accountId}`}
            className={twMerge(
                'relative flex w-80 shrink-0 flex-col overflow-hidden rounded-xl p-px shadow-sm ring-1 ring-inset ring-gray-200',
                !useCover && 'h-fit',
            )}
        >
            <div
                className={twMerge(
                    'h-full max-h-40 overflow-hidden transition-all duration-500',
                    useCover ? 'max-h-40 opacity-100' : 'max-h-0 opacity-0',
                )}
            >
                {useCover && <ProfileCoverImage />}
            </div>
            <div className="flex flex-col gap-5 p-6">
                <div className={twMerge('relative', useCover ? 'h-4' : 'h-20')}>
                    <div className="absolute -right-3 -top-3 flex h-full w-full justify-end">
                        <ProfileColor />
                        {!cover && <ProfileCoverIcon />}
                    </div>
                    <div className={twMerge('absolute', useCover && '-top-16')}>
                        <ProfileLogo />
                    </div>
                </div>

                <div className="flex flex-col gap-1">
                    <div className="flex max-w-full items-start gap-2">
                        <ProfileName />
                        {type === 'nonprofit' && <ProfileVerificationIcon />}
                    </div>
                    <ProfileTagLine />
                </div>
                <div className="flex flex-col gap-3">
                    <ProfileEmail />
                    <ProfilePhoneNumber />
                    <ProfileWebsite />
                </div>
            </div>
            <ProfileColorDisplay />
        </div>
    );
};

const ProfilePanelWrapper = () => {
    return (
        <PanelProvider>
            <ProfilePanel />
        </PanelProvider>
    );
};

export { ProfilePanelWrapper as ProfilePanel };
